import { useQuery, useMutation, useQueryClient } from 'react-query';
import { positionUnwinderAxiosApi } from 'src/Config/axiosApi';
import {
  UnwindingRule,
  UnwindingRuleType,
  UnwindingExecution,
  CreateUnwindingRuleDto,
  UpdateUnwindingRuleDto,
  OrdersResponse
} from '../types/unwinding.types';
import { normalizeOrdersPayload } from '../utils/normalizeOrders';
import {
  getUnwinderState,
  stopUnwinder,
  startUnwinder,
  UnwinderState
} from 'src/Api/exchanges';

export function useUnwindingRules(opts?: { ruleType?: UnwindingRuleType }) {
  const ruleType = opts?.ruleType;
  return useQuery<UnwindingRule[]>(
    ['unwinding-rules', ruleType ?? 'all'],
    async () => {
      const { data } = await positionUnwinderAxiosApi.get<UnwindingRule[]>(
        '/rules',
        ruleType ? { params: { ruleType } } : undefined
      );
      return data;
    },
    { refetchInterval: 5000, keepPreviousData: true }
  );
}

export function useCreateRule() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: CreateUnwindingRuleDto) => {
      const { data } = await positionUnwinderAxiosApi.post<UnwindingRule>(
        '/rules',
        dto
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('unwinding-rules');
      }
    }
  );
}

export function useUpdateRule() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, dto }: { id: string; dto: UpdateUnwindingRuleDto }) => {
      const { data } = await positionUnwinderAxiosApi.patch<UnwindingRule>(
        `/rules/${id}`,
        dto
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('unwinding-rules');
      }
    }
  );
}

export function useDeleteRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      await positionUnwinderAxiosApi.delete(`/rules/${id}`);
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('unwinding-rules');
      }
    }
  );
}

/**
 * Optimistic patcher shared by activate / pause / toggle-platform.
 * Walks every cached `unwinding-rules` query and patches the matching
 * rule by id. Returns a rollback fn the caller stashes for `onError`.
 *
 * Why: previously these mutations called `qc.invalidateQueries(...)`
 * on success, which kicked a fresh GET /rules round-trip that the
 * operator had to wait through (visible as a 1-3s lag on toggle).
 * The WS broadcast already pushes the authoritative state on the
 * next tick, so the cache patch is enough — we drop the invalidation
 * entirely. Rollback on error keeps the cache honest if the server
 * rejects.
 */
function patchUnwindingRulesCache(
  qc: ReturnType<typeof useQueryClient>,
  ruleId: string,
  patch: Partial<UnwindingRule>,
): () => void {
  const snapshots: Array<{ key: unknown; data: UnwindingRule[] | undefined }> =
    [];
  qc.getQueriesData<UnwindingRule[]>('unwinding-rules').forEach(
    ([key, data]) => {
      if (!Array.isArray(data)) return;
      snapshots.push({ key, data });
      qc.setQueryData<UnwindingRule[]>(
        key as any,
        data.map((r) => (r._id === ruleId ? { ...r, ...patch } : r)),
      );
    },
  );
  return () => {
    snapshots.forEach(({ key, data }) =>
      qc.setQueryData(key as any, data),
    );
  };
}

export function useActivateRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await positionUnwinderAxiosApi.post<UnwindingRule>(
        `/rules/${id}/activate`
      );
      return data;
    },
    {
      onMutate: async (id) => {
        await qc.cancelQueries('unwinding-rules');
        return { rollback: patchUnwindingRulesCache(qc, id, { status: 'active' as any }) };
      },
      onError: (_e, _v, ctx: any) => ctx?.rollback?.(),
    }
  );
}

export function usePauseRule() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await positionUnwinderAxiosApi.post<UnwindingRule>(
        `/rules/${id}/pause`
      );
      return data;
    },
    {
      onMutate: async (id) => {
        await qc.cancelQueries('unwinding-rules');
        return { rollback: patchUnwindingRulesCache(qc, id, { status: 'paused' as any }) };
      },
      onError: (_e, _v, ctx: any) => ctx?.rollback?.(),
    }
  );
}

export function useRuleExecutions(ruleId: string | null) {
  return useQuery<UnwindingExecution[]>(
    ['unwinding-executions', ruleId],
    async () => {
      if (!ruleId) return [];
      const { data } = await positionUnwinderAxiosApi.get<
        UnwindingExecution[]
      >(`/rules/${ruleId}/executions`);
      return data;
    },
    {
      enabled: !!ruleId,
      refetchInterval: 10000,
      keepPreviousData: true
    }
  );
}

export function usePanic() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await positionUnwinderAxiosApi.post('/rules/panic');
      return data;
    },
    { onSuccess: () => { qc.invalidateQueries('unwinding-rules'); } }
  );
}

export function useResume() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await positionUnwinderAxiosApi.post('/rules/resume');
      return data;
    },
    { onSuccess: () => { qc.invalidateQueries('unwinding-rules'); } }
  );
}

export function useGlobalToggle() {
  const qc = useQueryClient();
  return useMutation(
    async (enabled: boolean) => {
      const { data } = await positionUnwinderAxiosApi.post('/rules/global-toggle', { enabled });
      return data;
    },
    { onSuccess: () => { qc.invalidateQueries('unwinding-rules'); } }
  );
}

export function useTogglePlatform() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, platform, enabled }: { id: string; platform: 'kalshi' | 'polymarket'; enabled: boolean }) => {
      const { data } = await positionUnwinderAxiosApi.post(`/rules/${id}/toggle-platform`, { platform, enabled });
      return data;
    },
    {
      onMutate: async ({ id, platform, enabled }) => {
        await qc.cancelQueries('unwinding-rules');
        const patch: Partial<UnwindingRule> =
          platform === 'kalshi'
            ? { kalshiEnabled: enabled }
            : { polymarketEnabled: enabled };
        return { rollback: patchUnwindingRulesCache(qc, id, patch) };
      },
      onError: (_e, _v, ctx: any) => ctx?.rollback?.(),
    }
  );
}

export function useSetSimParams() {
  return useMutation(
    async ({
      pairId,
      tna,
      size,
      maxVol,
    }: {
      pairId: string;
      tna: number;
      size: number;
      maxVol?: number;
    }) => {
      const { data } = await positionUnwinderAxiosApi.post('/rules/sim-params', {
        pairId,
        tna,
        size,
        maxVol,
      });
      return data;
    }
  );
}

export function useApplySim() {
  return useMutation(async ({ pairId }: { pairId: string }) => {
    const { data } = await positionUnwinderAxiosApi.post(
      '/rules/apply-sim',
      { pairId }
    );
    return data;
  });
}

export function useOrders() {
  return useQuery<OrdersResponse>(
    'unwinding-orders',
    async () => {
      const { data } = await positionUnwinderAxiosApi.get('/orders');
      return normalizeOrdersPayload(data);
    },
    { refetchInterval: 3000, keepPreviousData: true }
  );
}

export function useUnwinderState() {
  return useQuery<UnwinderState>(
    ['unwinder-state'],
    getUnwinderState,
    {
      // Poll every 3s so the Stop/Start button flips promptly after a scale
      // patch lands on the Deployment.
      refetchInterval: 3_000,
      keepPreviousData: true,
    }
  );
}

export function useStopUnwinder() {
  const qc = useQueryClient();
  return useMutation(stopUnwinder, {
    onSuccess: job => {
      // Seed system/job so UpdatesProvider's stepper picks it up immediately.
      // UnwindingPage is wrapped in `<UpdatesProvider scope="predictions">`,
      // and the provider's job-poll query key is `['system/job', scope]` —
      // a string-only key like 'system/job' silently misses, leaving the
      // stepper popover invisible until the next 2s poll tick.
      qc.setQueryData(['system/job', 'predictions'], job);
      qc.invalidateQueries(['system/job', 'predictions']);
      qc.invalidateQueries('unwinding-rules');
      qc.invalidateQueries('unwinding-orders');
      qc.invalidateQueries(['unwinder-state']);
    }
  });
}

export function useStartUnwinder() {
  const qc = useQueryClient();
  return useMutation(startUnwinder, {
    onSuccess: job => {
      qc.setQueryData(['system/job', 'predictions'], job);
      qc.invalidateQueries(['system/job', 'predictions']);
      qc.invalidateQueries(['unwinder-state']);
    }
  });
}
